import { Controller } from "@hotwired/stimulus"

const zxcvbn = require("zxcvbn")

export default class extends Controller {
  static targets = [
    "password",
    "passwordConfirmation",
    "meter",
    "passwordMessage",
    "passwordConfirmationMessage",
  ]
  static values = { minimumPasswordLength: Number }

  check() {
    const password = this.passwordTarget.value
    const confirmation = this.passwordConfirmationTarget.value

    if (password === "") {
      this.resetMeter()
      this.clearMessages()
      return
    }

    // Check minimum length first
    if (password.length < this.minimumPasswordLengthValue) {
      this.showPasswordStrengthMessage(
        -1,
        `Password must be at least ${this.minimumPasswordLengthValue} characters long`,
      )
      this.updateMeter(0)
      this.showMismatch(password, confirmation)
      return
    }

    const result = zxcvbn(password)

    const score = result.score
    const message = result.feedback.warning || "The password is weak"

    this.updateMeter(score)
    this.showPasswordStrengthMessage(score, message)
    this.showMismatch(password, confirmation)
  }

  showPasswordStrengthMessage(score, message) {
    if (score >= 3) {
      this.passwordMessageTarget.textContent = ""
    } else {
      this.passwordMessageTarget.textContent = message
    }
  }

  clearMessages() {
    this.passwordMessageTarget.textContent = ""
    this.passwordConfirmationMessageTarget.textContent = ""
  }

  showMismatch(password, confirmation) {
    if (password === confirmation || confirmation === "") {
      this.passwordConfirmationMessageTarget.textContent = ""
    } else {
      this.passwordConfirmationMessageTarget.textContent =
        "Passwords do not match."
    }
  }

  resetMeter() {
    const progressBar = this.meterTarget.querySelector('[role="progressbar"]')
    const progressFill = progressBar.querySelector(".size-full")

    // Reset to 0%
    progressFill.style.transform = "translateX(-100%)"
    progressBar.setAttribute("aria-valuenow", "0")

    // Reset to default/weak styling
    progressFill.className =
      "size-full rounded-md bg-fg-error-secondary transition duration-75 ease-linear"
    this.meterTarget.classList.add("hidden")
  }

  updateMeter(score) {
    const progressBar = this.meterTarget.querySelector('[role="progressbar"]')
    const progressFill = progressBar.querySelector(".size-full")

    let percentage, colorClass, ariaValue

    switch (score) {
    case 0:
      percentage = 1
      colorClass = "bg-fg-error-secondary"
      ariaValue = 1
      break
    case 1:
      percentage = 25
      colorClass = "bg-fg-error-secondary"
      ariaValue = 25
      break
    case 2:
      percentage = 50
      colorClass = "bg-fg-warning-secondary"
      ariaValue = 50
      break
    case 3:
      percentage = 75
      colorClass = "bg-fg-warning-secondary"
      ariaValue = 75
      break
    case 4:
      percentage = 100
      colorClass = "bg-fg-success-secondary"
      ariaValue = 100
      break
    default:
      percentage = 0
      colorClass = "bg-fg-error-secondary"
      ariaValue = 0
    }

    // Update the transform to show the appropriate percentage
    const translateX = -(100 - percentage)
    progressFill.style.transform = `translateX(${translateX}%)`

    // Update ARIA value for accessibility
    progressBar.setAttribute("aria-valuenow", ariaValue.toString())

    // Update color class
    progressFill.className = `size-full rounded-md ${colorClass} transition duration-75 ease-linear`
    this.meterTarget.classList.remove("hidden")
  }
}
